/*
 * basis-toc.js
 * ──────────────────────────────────────────────────────────────────
 * Génère la table des matières d'article basis-toc.
 *
 * Pour chaque conteneur [data-basis-toc] :
 *   1. Lit le sélecteur de contenu (par défaut .article--content).
 *      Plusieurs conteneurs matchés sont scannés cumulativement
 *      (ex. sélecteur 'main' qui contient plusieurs sections).
 *   2. Scanne les H2 contenus, en filtrant :
 *        - les exclusions sentinelles hardcodées (header, footer,
 *          basis-toc lui-même, éléments cachés, drawers, popovers,
 *          composants techniques connus) ;
 *        - le sélecteur additionnel data-basis-toc-exclude éventuel.
 *   3. Attribue un id slugifié (FR-aware) à chaque H2 si absent.
 *   4. Injecte une liste de liens d'ancrage dans [data-basis-toc-list].
 *   5. Si aucun H2 retenu, masque le conteneur via l'attribut hidden.
 *
 * Vanilla, defer, sans dépendance externe.
 * ──────────────────────────────────────────────────────────────────
 */
(function () {
  'use strict';

  // Exclusions toujours appliquées : zones non éditoriales, composants
  // techniques, éléments cachés. Évite de polluer la TOC avec des H2
  // de site-header/footer, drawer, modale, atc-banner, etc. Ces sélecteurs
  // protègent aussi la TOC contre l'indexation de son propre H2 « Sommaire ».
  //
  // ⚠️ NE PAS exclure les balises génériques <header> / <footer> ici :
  // plusieurs sections basis-* utilisent <header class="...__head"> pour
  // envelopper un H2 éditorialement légitime (cf. basis-comparatifSources,
  // basis-comparatif, basis-compare-items, basis-how-to-zones-accordeon).
  // Cibler uniquement les classes dédiées au header/footer du site Focal.
  var ALWAYS_EXCLUDE = [
    '.basis-toc',
    '.site-header',
    '.site-footer',
    '[hidden]',
    '[aria-hidden="true"]',
    'dialog',
    'x-drawer',
    'x-popover',
    '.atc-banner--product',
    '.atc--product-details',
    '.disclosure',
    '[data-faq-ask]',
    '.faq-ask'
  ].join(', ');

  // Slugifie un texte FR : NFD pour séparer les diacritiques,
  // suppression des accents, lowercase, [^a-z0-9] -> '-'.
  function slugify(text) {
    return text
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  }

  // Récupère tous les H2 contenus dans la (ou les) zone(s) ciblée(s)
  // par contentSelector. Un même H2 ne doit apparaître qu'une fois
  // même si plusieurs zones se chevauchent.
  function collectHeadings(contentSelector) {
    var zones = document.querySelectorAll(contentSelector);
    if (!zones.length) {
      return [];
    }
    var seen = new Set();
    var out = [];
    zones.forEach(function (zone) {
      zone.querySelectorAll('h2').forEach(function (h) {
        if (!seen.has(h)) {
          seen.add(h);
          out.push(h);
        }
      });
    });
    return out;
  }

  // Vrai si le H2 doit être ignoré : ancêtre matchant les exclusions
  // sentinelles ou le sélecteur additionnel passé via data-attr.
  function isExcluded(heading, extraExclude) {
    if (heading.closest(ALWAYS_EXCLUDE)) {
      return true;
    }
    if (extraExclude && heading.closest(extraExclude)) {
      return true;
    }
    return false;
  }

  // Construit la liste d'ancres pour un conteneur TOC donné.
  function buildToc(toc) {
    var contentSelector = toc.getAttribute('data-basis-toc') || '.article--content';
    var extraExclude    = toc.getAttribute('data-basis-toc-exclude') || '';
    var list = toc.querySelector('[data-basis-toc-list]');

    if (!list) {
      toc.hidden = true;
      return;
    }

    var headings = collectHeadings(contentSelector);
    if (headings.length === 0) {
      toc.hidden = true;
      return;
    }

    var seen = Object.create(null);
    var frag = document.createDocumentFragment();
    var added = 0;

    headings.forEach(function (heading) {
      if (isExcluded(heading, extraExclude)) {
        return;
      }

      var text = (heading.textContent || '').trim();
      if (!text) {
        return;
      }

      // On préserve un id manuel posé par le rédacteur ; sinon on
      // génère un slug stable + suffixe numérique en cas de collision.
      var id = heading.id;
      if (!id) {
        var base = slugify(text) || 'section';
        id = base;
        var n = 2;
        while (seen[id] || document.getElementById(id)) {
          id = base + '-' + n;
          n += 1;
        }
        heading.id = id;
      }
      seen[id] = true;

      var li = document.createElement('li');
      li.className = 'basis-toc__item';

      var link = document.createElement('a');
      link.className = 'basis-toc__link';
      link.href = '#' + id;
      link.textContent = text;

      li.appendChild(link);
      frag.appendChild(li);
      added += 1;
    });

    if (added === 0) {
      toc.hidden = true;
      return;
    }

    list.appendChild(frag);
  }

  function init() {
    document.querySelectorAll('[data-basis-toc]').forEach(buildToc);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
